import React, { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
    CreditCard, TrendingUp, ArrowUpRight, ArrowDownRight, DollarSign, Loader2, Trash2, Pencil, Save,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtAED = (v) => `AED ${(v || 0).toLocaleString()}`;

const TYPE_CONFIG = {
    enrollment: { icon: CreditCard, color: 'text-blue-500', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', label: 'Enrollment' },
    upgrade: { icon: TrendingUp, color: 'text-violet-500', badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300', label: 'Upgrade' },
    redeposit: { icon: ArrowUpRight, color: 'text-emerald-500', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', label: 'Redeposit' },
    withdrawal: { icon: ArrowDownRight, color: 'text-red-500', badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', label: 'Withdrawal' },
};

function getConfig(type) {
    return TYPE_CONFIG[type] || { icon: DollarSign, color: 'text-gray-500', badge: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', label: type || 'Other' };
}

export function TransactionHistory({ studentId, isSuperAdmin = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteReason, setDeleteReason] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [editData, setEditData] = useState({ amount: '', date: '', description: '' });
    const [saving, setSaving] = useState(false);

    const fetchData = () => {
        if (!studentId) return;
        setLoading(true);
        setError(false);
        apiClient.get(`/students/${studentId}/transaction-history`)
            .then(r => setData(r.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData(); }, [studentId]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await apiClient.delete(
                `/students/${studentId}/transactions/${deleteTarget.id}?source=${deleteTarget.source}&reason=${encodeURIComponent(deleteReason)}`
            );
            toast.success('Transaction deleted');
            setDeleteTarget(null);
            setDeleteReason('');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete transaction');
        } finally {
            setDeleting(false);
        }
    };

    const openEdit = (txn) => {
        setEditTarget(txn);
        setEditData({
            amount: txn.amount_aed || txn.amount || '',
            date: txn.date || '',
            description: txn.label || '',
        });
    };

    const handleEdit = async () => {
        if (!editTarget) return;
        setSaving(true);
        try {
            await apiClient.put(`/students/${studentId}/transactions/${editTarget.id}`, {
                source: editTarget.source,
                amount: parseFloat(editData.amount) || 0,
                date: editData.date,
                description: editData.description,
            });
            toast.success('Transaction updated');
            setEditTarget(null);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-6 text-muted-foreground gap-2" data-testid="txn-history-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading history...
            </div>
        );
    }

    if (error || !data) {
        return <p className="text-xs text-muted-foreground text-center py-4">Unable to load transaction history</p>;
    }

    const { transactions, summary } = data;
    const txnCount = transactions?.length || 0;

    return (
        <div className="space-y-3" data-testid="transaction-history">
            {/* LTV Summary Header */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-emerald-500/10 to-sky-500/10 border border-emerald-500/20" data-testid="txn-summary">
                <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Total LTV</p>
                    <p className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400" data-testid="txn-ltv-value">
                        {fmtAED(summary?.net_value)}
                    </p>
                </div>
                <div className="text-right space-y-1">
                    <div className="flex items-center gap-2 justify-end">
                        <Badge variant="outline" className="text-[10px] font-mono" data-testid="txn-count-badge">
                            {txnCount} transaction{txnCount !== 1 ? 's' : ''}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                        <span className="text-emerald-600 font-mono">+{fmtAED(summary?.total_deposits)}</span>
                        {(summary?.total_withdrawals > 0) && (
                            <span className="text-red-500 font-mono">-{fmtAED(summary?.total_withdrawals)}</span>
                        )}
                    </div>
                </div>
            </div>

            {/* Compact Table */}
            {txnCount === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No transactions recorded</p>
            ) : (
                <ScrollArea className="max-h-[250px]">
                    <Table>
                        <TableHeader>
                            <TableRow className="text-xs">
                                <TableHead className="h-7 text-[10px] uppercase tracking-wider">Date</TableHead>
                                <TableHead className="h-7 text-[10px] uppercase tracking-wider">Type</TableHead>
                                <TableHead className="h-7 text-[10px] uppercase tracking-wider">Description</TableHead>
                                <TableHead className="h-7 text-[10px] uppercase tracking-wider text-right">Amount</TableHead>
                                {isSuperAdmin && <TableHead className="h-7 w-16 text-[10px] text-center">Actions</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {transactions.map((txn, i) => {
                                const cfg = getConfig(txn.type);
                                const isWithdrawal = txn.type === 'withdrawal';
                                const isEditable = txn.source && txn.source !== 'enrollment';
                                return (
                                    <TableRow key={txn.id || `txn-${i}`} className="text-xs hover:bg-muted/50" data-testid={`txn-row-${i}`}>
                                        <TableCell className="py-1.5 text-muted-foreground whitespace-nowrap">
                                            {txn.date ? new Date(txn.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : 'N/A'}
                                        </TableCell>
                                        <TableCell className="py-1.5">
                                            <Badge className={`text-[10px] px-1.5 py-0 border-0 ${cfg.badge}`}>
                                                {cfg.label}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="py-1.5 max-w-[180px] truncate" title={txn.label}>
                                            {txn.label}
                                        </TableCell>
                                        <TableCell className={`py-1.5 text-right font-mono font-medium ${isWithdrawal ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                            {isWithdrawal ? '-' : '+'}{fmtAED(txn.amount_aed)}
                                        </TableCell>
                                        {isSuperAdmin && (
                                            <TableCell className="py-1.5 px-1">
                                                <div className="flex items-center justify-center gap-0.5">
                                                    {isEditable && (
                                                        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-blue-500"
                                                            onClick={(e) => { e.stopPropagation(); openEdit(txn); }}
                                                            data-testid={`edit-txn-${i}`}>
                                                            <Pencil className="h-3 w-3" />
                                                        </Button>
                                                    )}
                                                    <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-red-500"
                                                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(txn); }}
                                                        data-testid={`delete-txn-${i}`}>
                                                        <Trash2 className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </ScrollArea>
            )}

            {/* Edit Dialog */}
            <Dialog open={!!editTarget} onOpenChange={(open) => { if (!open) setEditTarget(null); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Pencil className="h-4 w-4 text-blue-500" />
                            Edit Transaction
                        </DialogTitle>
                    </DialogHeader>
                    {editTarget && (
                        <div className="space-y-4 py-2">
                            <div className="p-3 rounded-lg bg-muted/50 border text-sm">
                                <p className="text-muted-foreground text-xs mb-1">Transaction</p>
                                <p className="font-medium">{getConfig(editTarget.type).label}: {editTarget.label}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Amount (AED)</Label>
                                    <Input type="number" value={editData.amount}
                                        onChange={e => setEditData({ ...editData, amount: e.target.value })}
                                        className="h-9" data-testid="edit-txn-amount" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Date</Label>
                                    <Input type="date" value={editData.date}
                                        onChange={e => setEditData({ ...editData, date: e.target.value })}
                                        className="h-9" data-testid="edit-txn-date" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Description</Label>
                                <Input value={editData.description}
                                    onChange={e => setEditData({ ...editData, description: e.target.value })}
                                    className="h-9" data-testid="edit-txn-desc" />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditTarget(null)} disabled={saving}>Cancel</Button>
                        <Button onClick={handleEdit} disabled={saving} data-testid="save-txn-edit">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteReason(''); } }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-red-600">Delete Transaction</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3">
                                <p>
                                    You are about to permanently delete this transaction. This action is logged and cannot be undone.
                                </p>
                                {deleteTarget && (
                                    <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-sm space-y-1">
                                        <p><strong>Type:</strong> {getConfig(deleteTarget.type).label}</p>
                                        <p><strong>Amount:</strong> {fmtAED(deleteTarget.amount_aed)}</p>
                                        <p><strong>Date:</strong> {deleteTarget.date}</p>
                                        <p><strong>Description:</strong> {deleteTarget.label}</p>
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <Label className="text-foreground">Reason for deletion *</Label>
                                    <Textarea
                                        value={deleteReason}
                                        onChange={(e) => setDeleteReason(e.target.value)}
                                        placeholder="e.g. Duplicate transaction, wrong amount entered..."
                                        rows={2}
                                        data-testid="delete-reason-input"
                                    />
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={deleting || !deleteReason.trim()}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="confirm-delete-txn"
                        >
                            {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                            Delete Transaction
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
